import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { Save, TestTube } from 'lucide-react';
import { toast } from 'sonner';

const TallyConfig = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    tally_url: 'http://localhost',
    port: 9000,
    username: '',
    password: '',
    sync_mode: 'batch',
    auto_sync: false
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await api.get('/tally/config');
      setConfig(response.data);
      setFormData({
        company_name: response.data.company_name,
        tally_url: response.data.tally_url,
        port: response.data.port,
        username: response.data.username || '',
        password: response.data.password || '',
        sync_mode: response.data.sync_mode,
        auto_sync: response.data.auto_sync
      });
    } catch (error) {
      console.error('No configuration found:', error);
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const response = await api.post('/tally/config/test-connection');
      if (response.data.success) {
        toast.error('✓ ' + response.data.message);
      } else {
        toast.error('✗ Connection failed: ' + response.data.message);
      }
    } catch (error) {
      toast.error('✗ Test failed: ' + (error.response?.data?.detail || 'Connection error'));
    } finally {
      setTesting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (config) {
        await api.put(`/tally/config/${config.id}`, formData);
      } else {
        await api.post('/tally/config', formData);
      }
      toast.error('Configuration saved successfully');
      fetchConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save configuration');
    }
  };

  if (loading) {
    return <div data-testid="loading-indicator">Loading configuration...</div>;
  }

  return (
    <div data-testid="tally-config-page">
      <h1 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '24px' }}>Tally Configuration</h1>

      <div className="card" style={{ maxWidth: '700px' }}>
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-2">
            <div className="form-group">
              <label className="form-label">Company Name *</label>
              <input
                type="text"
                className="form-input"
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                required
                placeholder="As per Tally"
                data-testid="company-name-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Tally URL *</label>
              <input
                type="text"
                className="form-input"
                value={formData.tally_url}
                onChange={(e) => setFormData({ ...formData, tally_url: e.target.value })}
                required
                placeholder="http://localhost"
                data-testid="tally-url-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-2">
            <div className="form-group">
              <label className="form-label">Port *</label>
              <input
                type="number"
                className="form-input"
                value={formData.port}
                onChange={(e) => setFormData({ ...formData, port: parseInt(e.target.value) })}
                required
                data-testid="port-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Sync Mode *</label>
              <select
                className="form-select"
                value={formData.sync_mode}
                onChange={(e) => setFormData({ ...formData, sync_mode: e.target.value })}
                data-testid="sync-mode-select"
              >
                <option value="real-time">Real-time</option>
                <option value="batch">Batch</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2">
            <div className="form-group">
              <label className="form-label">Username (Optional)</label>
              <input
                type="text"
                className="form-input"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                placeholder="If Tally requires auth"
                data-testid="username-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Password (Optional)</label>
              <input
                type="password"
                className="form-input"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="If Tally requires auth"
                data-testid="password-input"
              />
            </div>
          </div>

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
              <input
                type="checkbox"
                checked={formData.auto_sync}
                onChange={(e) => setFormData({ ...formData, auto_sync: e.target.checked })}
                data-testid="auto-sync-checkbox"
              />
              Enable automatic synchronization
            </label>
            <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '4px', marginLeft: '24px' }}>
              Automatically sync invoices and stock on creation/approval
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
            <button type="submit" className="btn btn-primary" data-testid="save-config-btn">
              <Save size={16} />
              Save Configuration
            </button>
            <button
              type="button"
              onClick={testConnection}
              className="btn btn-secondary"
              disabled={testing}
              data-testid="test-connection-btn"
            >
              <TestTube size={16} />
              {testing ? 'Testing...' : 'Test Connection'}
            </button>
          </div>
        </form>
      </div>

      {/* Help Section */}
      <div className="card" style={{ marginTop: '24px', maxWidth: '700px', backgroundColor: 'var(--bg-surface-alt)' }}>
        <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px' }}>Setup Instructions</div>
        <ol style={{ fontSize: '13px', lineHeight: '1.8', paddingLeft: '20px' }}>
          <li>Ensure Tally is running and configured to accept HTTP requests</li>
          <li>Enable ODBC Server in Tally: Gateway of Tally → F12 (Configure) → Advanced Configuration → ODBC</li>
          <li>Default Tally port is 9000 (can be changed in Tally settings)</li>
          <li>Enter the exact company name as configured in Tally</li>
          <li>Test connection to verify setup</li>
        </ol>
      </div>
    </div>
  );
};

export default TallyConfig;
